import { useState } from "react";
import { Box, Paper, TextField, Button, Typography, Link } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { DELTA_ACCENT, DELTA_PRIMARY, TEXT_SECONDARY } from "../colors";

type Mode = "signIn" | "signUp" | "confirmSignUp" | "forgotPassword" | "resetPassword";

export const LoginPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState<Mode>(searchParams.get("mode") === "signUp" ? "signUp" : "signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState<string>();
  const [info, setInfo] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  const withSubmitHandling = (fn: () => Promise<void>) => async () => {
    setError(undefined);
    setSubmitting(true);
    try {
      await fn();
    } catch (e: any) {
      setError(e?.message ?? "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignIn = withSubmitHandling(async () => {
    await auth.signIn(email, password);
    navigate("/");
  });

  const handleSignUp = withSubmitHandling(async () => {
    await auth.signUp(email, password);
    setInfo(undefined);
    setMode("confirmSignUp");
  });

  const handleConfirmSignUp = withSubmitHandling(async () => {
    await auth.confirmSignUp(email, code);
    setCode("");
    setInfo("Email confirmed - you can now sign in.");
    setMode("signIn");
  });

  const handleForgotPassword = withSubmitHandling(async () => {
    await auth.forgotPassword(email);
    setInfo(undefined);
    setMode("resetPassword");
  });

  const handleResetPassword = withSubmitHandling(async () => {
    await auth.confirmForgotPassword(email, code, newPassword);
    setCode("");
    setNewPassword("");
    setInfo("Password reset - you can now sign in with your new password.");
    setMode("signIn");
  });

  return (
    <Box style={{ display: "flex", justifyContent: "center", paddingTop: 20 }}>
      <Paper elevation={0} style={{ padding: 32, maxWidth: 420, width: "100%" }}>
        <Typography variant="h5" style={{ color: DELTA_PRIMARY, fontWeight: 700, marginBottom: 8 }}>
          {mode === "signIn" && "Sign In"}
          {mode === "signUp" && "Create Account"}
          {mode === "confirmSignUp" && "Confirm Your Email"}
          {mode === "forgotPassword" && "Forgot Password"}
          {mode === "resetPassword" && "Reset Password"}
        </Typography>

        {mode === "signIn" && (
          <Typography variant="body2" style={{ color: TEXT_SECONDARY, marginBottom: 24 }}>
            Sign in to manage your account and subscription.
          </Typography>
        )}
        {mode === "signUp" && (
          <Typography variant="body2" style={{ color: TEXT_SECONDARY, marginBottom: 24 }}>
            Create a free account, then subscribe to Pro or Enterprise whenever you're ready.
          </Typography>
        )}
        {mode === "confirmSignUp" && (
          <Typography variant="body2" style={{ color: TEXT_SECONDARY, marginBottom: 24 }}>
            We sent a confirmation code to {email}. Enter it below to finish creating your account.
          </Typography>
        )}
        {mode === "forgotPassword" && (
          <Typography variant="body2" style={{ color: TEXT_SECONDARY, marginBottom: 24 }}>
            Enter your email and we'll send you a code to reset your password.
          </Typography>
        )}
        {mode === "resetPassword" && (
          <Typography variant="body2" style={{ color: TEXT_SECONDARY, marginBottom: 24 }}>
            Enter the code we sent to {email} and choose a new password.
          </Typography>
        )}

        {info && (
          <Typography variant="body2" style={{ color: "#2E7D32", marginBottom: 16 }}>
            {info}
          </Typography>
        )}
        {error && (
          <Typography variant="body2" style={{ color: "#C62828", marginBottom: 16 }}>
            {error}
          </Typography>
        )}

        <Box style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {(mode === "signIn" || mode === "signUp" || mode === "forgotPassword") && (
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              size="small"
            />
          )}

          {(mode === "signIn" || mode === "signUp") && (
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              size="small"
            />
          )}

          {(mode === "confirmSignUp" || mode === "resetPassword") && (
            <TextField
              label="Confirmation Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              fullWidth
              size="small"
            />
          )}

          {mode === "resetPassword" && (
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              size="small"
            />
          )}

          {mode === "signIn" && (
            <Button
              variant="contained"
              disabled={submitting}
              onClick={handleSignIn}
              style={{ backgroundColor: DELTA_ACCENT }}
            >
              Sign In
            </Button>
          )}
          {mode === "signUp" && (
            <Button
              variant="contained"
              disabled={submitting}
              onClick={handleSignUp}
              style={{ backgroundColor: DELTA_ACCENT }}
            >
              Create Account
            </Button>
          )}
          {mode === "confirmSignUp" && (
            <Button
              variant="contained"
              disabled={submitting}
              onClick={handleConfirmSignUp}
              style={{ backgroundColor: DELTA_ACCENT }}
            >
              Confirm
            </Button>
          )}
          {mode === "forgotPassword" && (
            <Button
              variant="contained"
              disabled={submitting}
              onClick={handleForgotPassword}
              style={{ backgroundColor: DELTA_ACCENT }}
            >
              Send Reset Code
            </Button>
          )}
          {mode === "resetPassword" && (
            <Button
              variant="contained"
              disabled={submitting}
              onClick={handleResetPassword}
              style={{ backgroundColor: DELTA_ACCENT }}
            >
              Reset Password
            </Button>
          )}
        </Box>

        <Box style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 8 }}>
          {mode === "signIn" && (
            <>
              <Link component="button" variant="body2" onClick={() => { setError(undefined); setInfo(undefined); setMode("forgotPassword"); }}>
                Forgot password?
              </Link>
              <Link component="button" variant="body2" onClick={() => { setError(undefined); setInfo(undefined); setMode("signUp"); }}>
                Need an account? Sign up
              </Link>
            </>
          )}
          {(mode === "signUp" || mode === "forgotPassword" || mode === "resetPassword" || mode === "confirmSignUp") && (
            <Link component="button" variant="body2" onClick={() => { setError(undefined); setInfo(undefined); setMode("signIn"); }}>
              Back to sign in
            </Link>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginPage;
